
import { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{
    error: any;
    success: boolean;
  }>;
  signUp: (email: string, password: string) => Promise<{
    error: any;
    success: boolean;
  }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to clean up auth state - prevents auth limbo issues
const cleanupAuthState = () => {
  // Remove all Supabase auth keys from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Also clean sessionStorage if used
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Check admin status when user changes
  useEffect(() => {
    if (!session?.user) {
      setIsAdmin(false);
      return;
    }

    const checkAdminStatus = async () => {
      try {
        // Use the has_role function we created in the SQL migration
        const { data, error } = await supabase.rpc('has_role', {
          check_user_id: session.user.id,
          check_role: 'admin'
        });

        if (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
          return;
        }

        setIsAdmin(!!data);
      } catch (error) {
        console.error('Error in admin check:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [session]);

  useEffect(() => {
    // Set up auth state listener FIRST to prevent missing auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Defer data fetching to prevent deadlocks
          setTimeout(() => {
            // Re-check admin status after sign-in
            if (currentSession?.user) {
              supabase.rpc('has_role', {
                check_user_id: currentSession.user.id,
                check_role: 'admin'
              }).then(({ data }) => {
                setIsAdmin(!!data);
              });
            }
          }, 0);
        }
        
        if (event === 'SIGNED_OUT') {
          setIsAdmin(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Clean up existing auth state to prevent conflicts
      cleanupAuthState();
      
      // Attempt global sign out first
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (error) {
        // Continue even if this fails
      }
      
      // Sign in with email and password
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      return {
        error,
        success: !error,
      };
    } catch (error) {
      return {
        error,
        success: false,
      };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      // Clean up existing auth state to prevent conflicts
      cleanupAuthState();
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      return {
        error,
        success: !error,
      };
    } catch (error) {
      return {
        error,
        success: false,
      };
    }
  };

  const signOut = async () => {
    try {
      // Clean up auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      await supabase.auth.signOut({ scope: 'global' });
      
      // Force navigation to clear application state
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Error during sign out. Please try again.");
    }
  };

  return (
    <AuthContext.Provider value={{ session, user, isLoading, isAdmin, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
